import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { show_alerta } from '../../funciones.js';
import useCursoData from '../../hooks/useCursoData';
import CONFIG from '../../config';
import PerfilLogo from '../usuarios/PerfilLogo';

const URL_TRAYECTORIAS = `${CONFIG.API_URL}/operarTrayectorias.php`;
const MySwal = withReactContent(Swal);

function Trayectorias({ idMC, rol, idCG, configuracion, idTrayectoriaInicial = null }) {
    const esDocenteOAdmin = rol == 6 || rol == 5 || rol <= 4;
    const { estudiantesCurso } = useCursoData(idCG);

    const [trayectorias, setTrayectorias] = useState([]);
    const [instancias, setInstancias] = useState([]);
    const [espera, setEspera] = useState(false);
    const [modo, setModo] = useState('list'); // 'list', 'crear', 'evaluar', 'ver_estudiante'

    // Formulario de Trayectoria
    const [trayectoriaId, setTrayectoriaId] = useState(0);
    const [idInstancia, setIdInstancia] = useState('');
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [visibleEstudiante, setVisibleEstudiante] = useState(false);
    const [criteriosTemporales, setCriteriosTemporales] = useState([]);

    // Selección para evaluación
    const [trayectoriaSeleccionada, setTrayectoriaSeleccionada] = useState(null);
    const [estudianteSeleccionado, setEstudianteSeleccionado] = useState(null);
    const [criteriosEvaluacion, setCriteriosEvaluacion] = useState([]); // incluye calificacion y comentario del estudiante
    const [estudiantesEvaluadosIds, setEstudiantesEvaluadosIds] = useState([]); // IDs de estudiantes con evaluación cargada

    // Cargar trayectorias e instancias al inicializar
    useEffect(() => {
        cargarTrayectorias();
        cargarInstancias();
    }, [idCG]);

    // Si viene un idTrayectoriaInicial (ej: clic desde el Muro)
    useEffect(() => {
        if (idTrayectoriaInicial && trayectorias.length > 0) {
            const encontrada = trayectorias.find(t => String(t.id) === String(idTrayectoriaInicial));
            if (encontrada) {
                if (esDocenteOAdmin) {
                    iniciarEvaluacion(encontrada);
                } else {
                    verDetalleTrayectoria(encontrada);
                }
            }
        }
    }, [idTrayectoriaInicial, trayectorias]);

    const cargarInstancias = async (idTrayectoriaEditar = 0) => {
        try {
            const cicloActual = new Date().getFullYear();
            const loggeduserCurso = localStorage.getItem('loggeduserCurso') || '';
            const res = await axios.get(`${URL_TRAYECTORIAS}?modo=instancias&id_curso_grupo=${idCG}&id_curso=${loggeduserCurso}&ciclo=${cicloActual}&id_trayectoria=${idTrayectoriaEditar}`);
            console.log("Respuesta completa de Instancias:", res.data);

            let listaInstancias = [];
            if (Array.isArray(res.data)) {
                listaInstancias = res.data;
            } else if (res.data && Array.isArray(res.data.instancias)) {
                listaInstancias = res.data.instancias;
            }

            listaInstancias.sort((a, b) => {
                const fA = a.fecha_inicio || a.fechaDesde ? new Date(a.fecha_inicio || a.fechaDesde).getTime() : 0;
                const fB = b.fecha_inicio || b.fechaDesde ? new Date(b.fecha_inicio || b.fechaDesde).getTime() : 0;
                return fA - fB;
            });
            console.log("Instancias procesadas:", listaInstancias);
            setInstancias(listaInstancias);
            return listaInstancias;
        } catch (err) {
            console.error("Error al cargar instancias:", err);
            return [];
        }
    };

    const cargarTrayectorias = async () => {
        setEspera(true);
        try {
            const timestamp = new Date().getTime();
            const res = await axios.get(`${URL_TRAYECTORIAS}?id_curso_grupo=${idCG}&t=${timestamp}`);
            if (Array.isArray(res.data)) {
                setTrayectorias(res.data);
            } else {
                setTrayectorias([]);
            }
        } catch (err) {
            console.error("Error al cargar trayectorias:", err);
            show_alerta("Error al cargar las trayectorias", "error");
        } finally {
            setEspera(false);
        }
    };

    // Crear / Editar Trayectoria
    const iniciarCreacion = async () => {
        setTrayectoriaId(0);
        setTitulo('');
        setDescripcion('');
        setVisibleEstudiante(false);
        setCriteriosTemporales([
            { criterio: '', descripcion: '' }
        ]);

        setEspera(true);
        try {
            const instList = await cargarInstancias(0);
            if (instList && instList.length > 0) {
                setIdInstancia(instList[0].id);
            } else {
                setIdInstancia('');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setEspera(false);
        }
        setModo('crear');
    };

    const iniciarEdicion = async (tray) => {
        setEspera(true);
        try {
            const res = await axios.get(`${URL_TRAYECTORIAS}?id_trayectoria=${tray.id}`);
            if (res.data && !res.data.error) {
                const tInfo = res.data.trayectoria;
                setTrayectoriaId(tInfo.id);
                setIdInstancia(tInfo.id_instancia || '');
                setTitulo(tInfo.titulo);
                setDescripcion(tInfo.descripcion || '');
                setVisibleEstudiante(tInfo.visible_estudiante == 1);
                setCriteriosTemporales(res.data.criterios.map(c => ({
                    criterio: c.criterio,
                    descripcion: c.descripcion || ''
                })));

                // Cargar instancias disponibles incluyendo la instancia que tiene esta trayectoria asignada
                const instList = await cargarInstancias(tInfo.id);

                let finalInstancias = instList;
                if (tInfo.id_instancia && !instList.some(i => String(i.id) === String(tInfo.id_instancia))) {
                    const instExtra = {
                        id: tInfo.id_instancia,
                        nombre_instancia: tInfo.instancia_nombre || `Instancia #${tInfo.id_instancia}`,
                        tipo_calificacion: tInfo.instancia_tipo_calificacion || ''
                    };
                    finalInstancias = [instExtra, ...instList];
                }
                setInstancias(finalInstancias);

                setModo('crear');
            } else {
                show_alerta("Error al cargar datos de la trayectoria", "error");
            }
        } catch (err) {
            console.error(err);
            show_alerta("Error al conectar con el servidor", "error");
        } finally {
            setEspera(false);
        }
    };

    const agregarCriterioFila = () => {
        setCriteriosTemporales([
            ...criteriosTemporales,
            { criterio: '', descripcion: '' }
        ]);
    };

    const eliminarCriterioFila = (index) => {
        if (criteriosTemporales.length === 1) {
            show_alerta("Una trayectoria debe tener al menos un criterio", "warning");
            return;
        }
        setCriteriosTemporales(criteriosTemporales.filter((_, i) => i !== index));
    };

    const handleCriterioCambio = (index, campo, valor) => {
        const nuevos = [...criteriosTemporales];
        nuevos[index][campo] = valor;
        setCriteriosTemporales(nuevos);
    };

    const guardarTrayectoria = async (e) => {
        e.preventDefault();
        if (!idInstancia) {
            show_alerta("Debe seleccionar una instancia de evaluación", "warning");
            return;
        }
        if (!titulo.trim()) {
            show_alerta("El título es obligatorio", "warning");
            return;
        }

        // Validar criterios
        for (let i = 0; i < criteriosTemporales.length; i++) {
            const crit = criteriosTemporales[i];
            if (!crit.criterio.trim()) {
                show_alerta(`El criterio #${i + 1} no tiene nombre`, "warning");
                return;
            }
        }

        setEspera(true);
        try {
            const payload = {
                accion: 'guardar_trayectoria',
                id: trayectoriaId,
                id_curso_grupo: idCG,
                id_instancia: idInstancia,
                titulo: titulo,
                descripcion: descripcion,
                visible_estudiante: visibleEstudiante ? 1 : 0,
                criterios: criteriosTemporales
            };

            const formData = new FormData();
            formData.append('data', JSON.stringify(payload));

            const res = await axios.post(URL_TRAYECTORIAS, formData);
            if (res.data && res.data.success) {
                show_alerta("Registro de trayectoria guardado con éxito", "success");
                setModo('list');
                cargarTrayectorias();
            } else {
                show_alerta(res.data?.msg || "Error al guardar la trayectoria", "error");
            }
        } catch (err) {
            console.error(err);
            show_alerta("Error al conectar con el servidor", "error");
        } finally {
            setEspera(false);
        }
    };

    const eliminarTrayectoria = (id) => {
        MySwal.fire({
            title: '¿Estás seguro de eliminar este registro de trayectoria?',
            text: "Se eliminarán permanentemente todas las evaluaciones asociadas a esta trayectoria.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            customClass: {
                confirmButton: 'btn btn-danger mx-2',
                cancelButton: 'btn btn-outline-secondary mx-2'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                setEspera(true);
                try {
                    const res = await axios.delete(URL_TRAYECTORIAS, { data: { id } });
                    if (res.data && res.data.success) {
                        show_alerta("Trayectoria eliminada con éxito", "success");
                        cargarTrayectorias();
                    } else {
                        show_alerta(res.data?.msg || "Error al eliminar la trayectoria", "error");
                    }
                } catch (err) {
                    console.error(err);
                    show_alerta("Error de conexión", "error");
                } finally {
                    setEspera(false);
                }
            }
        });
    };

    // Panel de Evaluación
    const iniciarEvaluacion = async (tray) => {
        setTrayectoriaSeleccionada(tray);
        setEstudianteSeleccionado(null);
        setCriteriosEvaluacion([]);
        setModo('evaluar');

        try {
            const timestamp = new Date().getTime();
            const res = await axios.get(`${URL_TRAYECTORIAS}?id_trayectoria=${tray.id}&t=${timestamp}`);
            if (res.data && Array.isArray(res.data.estudiantes_evaluados)) {
                setEstudiantesEvaluadosIds(res.data.estudiantes_evaluados);
            } else {
                setEstudiantesEvaluadosIds([]);
            }
        } catch (err) {
            console.error("Error al obtener los estudiantes evaluados:", err);
        }
    };

    const seleccionarEstudianteParaEvaluar = async (estudiante) => {
        setEspera(true);
        setEstudianteSeleccionado(estudiante);
        try {
            const timestamp = new Date().getTime();
            const res = await axios.get(`${URL_TRAYECTORIAS}?id_trayectoria=${trayectoriaSeleccionada.id}&id_estudiante=${estudiante.id}&t=${timestamp}`);
            if (res.data && !res.data.error) {
                const nivelOrder = { 'sobresaliente': 1, 'ms': 1, 'sup': 1, 'muy bueno': 2, 's': 2, 'bueno': 3, 'ep': 3, 'regular': 4, 'sc': 4, 'pendiente': 5 };
                const list = (res.data.criterios || []).map(c => ({
                    ...c,
                    calificacion: c.calificacion ? c.calificacion : '-',
                    comentario: c.comentario !== null ? c.comentario : ''
                }));
                list.sort((a, b) => {
                    const nameA = String(a.criterio || '').trim().toLowerCase();
                    const nameB = String(b.criterio || '').trim().toLowerCase();
                    const orderA = nivelOrder[nameA] !== undefined ? nivelOrder[nameA] : 99;
                    const orderB = nivelOrder[nameB] !== undefined ? nivelOrder[nameB] : 99;
                    if (orderA !== orderB) return orderA - orderB;
                    return (Number(b.id) || 0) - (Number(a.id) || 0);
                });
                setCriteriosEvaluacion(list);
            } else {
                show_alerta("Error al cargar la evaluación del estudiante", "error");
            }
        } catch (err) {
            console.error(err);
            show_alerta("Error de conexión", "error");
        } finally {
            setEspera(false);
        }
    };

    // Regla de evaluación excluyente: Si un criterio se marca como "Logrado", el resto pasa automáticamente a "-"
    const handleEvaluacionCambio = (indexTarget, campo, valor) => {
        const nuevos = criteriosEvaluacion.map((crit, idx) => {
            if (campo === 'calificacion') {
                if (idx === indexTarget) {
                    return { ...crit, calificacion: valor };
                } else if (valor === 'Logrado') {
                    // Si el criterio objetivo se pone en Logrado, los demás cambian a '-'
                    return { ...crit, calificacion: '-' };
                }
                return crit;
            } else {
                if (idx === indexTarget) {
                    return { ...crit, [campo]: valor };
                }
                return crit;
            }
        });
        setCriteriosEvaluacion(nuevos);
    };

    const guardarEvaluacionEstudiante = async () => {
        if (!estudianteSeleccionado) return;

        setEspera(true);
        try {
            const payload = {
                accion: 'guardar_evaluacion',
                id_estudiante: estudianteSeleccionado.id,
                evaluaciones: criteriosEvaluacion.map(c => ({
                    id_criterio: c.id,
                    calificacion: c.calificacion || '-',
                    comentario: c.comentario
                }))
            };

            const formData = new FormData();
            formData.append('data', JSON.stringify(payload));

            const res = await axios.post(URL_TRAYECTORIAS, formData);
            if (res.data && res.data.success) {
                show_alerta("Evaluación guardada con éxito", "success");
                const tieneLogrado = criteriosEvaluacion.some(c => c.calificacion === 'Logrado');
                const estId = estudianteSeleccionado.id;
                if (tieneLogrado) {
                    setEstudiantesEvaluadosIds(prev => prev.some(id => String(id) === String(estId)) ? prev : [...prev, estId]);
                } else {
                    setEstudiantesEvaluadosIds(prev => prev.filter(id => String(id) !== String(estId)));
                }
                seleccionarEstudianteParaEvaluar(estudianteSeleccionado);
            } else {
                show_alerta(res.data?.msg || "Error al guardar la evaluación", "error");
            }
        } catch (err) {
            console.error(err);
            show_alerta("Error al conectar con el servidor", "error");
        } finally {
            setEspera(false);
        }
    };

    // Obtener opciones de nivel según el tipo de calificación de la instancia seleccionada
    const obtenerOpcionesNivel = (instId) => {
        const inst = instancias.find(i => String(i.id) === String(instId));
        if (!inst) return [];
        const tipo = inst.tipo_calificacion;
        if (tipo === 'Valorativa' || tipo === 'Valorativa 1' || tipo === 'Valorativa 2') {
            const defaultOp = tipo === 'Valorativa 2' ? ['TED', 'TEP', 'TEA'] : ['SC', 'EP', 'S', 'MS', 'Sup'];
            const rawVal = tipo === 'Valorativa 2' ? configuracion?.formato_valoracion2 : configuracion?.formato_valoracion;
            if (rawVal && typeof rawVal === 'string' && rawVal.trim() !== '') {
                try {
                    const jsonStr = rawVal.trim().replace(/'/g, '"');
                    const parsed = JSON.parse(jsonStr);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return parsed.filter(x => x !== '');
                    }
                } catch (e) {
                    const splitted = rawVal.trim().split(',').map(s => s.trim().replace(/^['"]|['"]$/g, '')).filter(x => x !== '');
                    if (splitted.length > 0) return splitted;
                }
            }
            return defaultOp;
        } else if (tipo === 'Numerica') {
            return ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
        } else if (tipo === 'Logro') {
            return ['Logrado', 'pendiente'];
        } else if (tipo === 'Participacion') {
            return ['Participo', 'no participo'];
        }
        return [];
    };

    // Estudiante / Visualización de Trayectoria
    const verDetalleTrayectoria = async (tray) => {
        setEspera(true);
        setTrayectoriaSeleccionada(tray);
        try {
            const timestamp = new Date().getTime();
            const loggedUserId = localStorage.getItem('loggedUserId') || '';
            const res = await axios.get(`${URL_TRAYECTORIAS}?id_trayectoria=${tray.id}&id_estudiante=${loggedUserId}&t=${timestamp}`);
            if (res.data && !res.data.error) {
                setCriteriosEvaluacion(res.data.criterios);
                setModo('ver_estudiante');
            } else {
                show_alerta("Error al cargar la trayectoria", "error");
            }
        } catch (err) {
            console.error(err);
            show_alerta("Error de conexión", "error");
        } finally {
            setEspera(false);
        }
    };

    // Verificar si el período de registro de la instancia está activo
    const esPeriodoActivo = (tray) => {
        if (!tray || !tray.instancia_fecha_inicio || !tray.instancia_fecha_cierre) return true;
        const fechaActual = new Date();
        const fechaInicio = new Date(tray.instancia_fecha_inicio);
        const fechaCierre = new Date(tray.instancia_fecha_cierre);
        return fechaActual >= fechaInicio && fechaActual <= fechaCierre;
    };

    return (
        <div className="container-fluid p-3">
            {/* ENCABEZADO CON NAVEGACIÓN DE RETORNO Y BREADCRUMB */}
            <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-3 shadow-sm border">
                <div>
                    <h4 className="fw-bold text-dark mb-1">
                        <i className="fa-solid fa-route text-success me-2"></i>
                        Registro de Trayectorias Educativas
                    </h4>
                    <p className="text-secondary small mb-0">
                        Evaluación y seguimiento de instancias pedagógicas por estudiante
                    </p>
                </div>

                <div className="d-flex gap-2">
                    {modo !== 'list' && (
                        <button
                            className="btn btn-outline-secondary rounded-pill px-3"
                            onClick={() => { setModo('list'); cargarTrayectorias(); }}
                            type="button"
                        >
                            <i className="fa-solid fa-arrow-left me-1"></i> Volver a Trayectorias
                        </button>
                    )}

                    {modo === 'list' && esDocenteOAdmin && (
                        <button
                            className="btn btn-success rounded-pill px-3"
                            onClick={iniciarCreacion}
                            type="button"
                        >
                            <i className="fa-solid fa-plus me-1"></i> Crear Registro de Trayectoria
                        </button>
                    )}
                </div>
            </div>

            {/* CONTENIDO PRINCIPAL */}
            <div className="bg-white p-4 rounded-3 shadow-sm border">
                {espera && (
                    <div className="d-flex justify-content-center my-5">
                        <div className="spinner-border text-success" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                )}

                {/* MODO LISTADO DE TRAYECTORIAS */}
                {!espera && modo === 'list' && (
                    <div>
                        {trayectorias.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fa-solid fa-folder-open text-muted fa-3x mb-3"></i>
                                <h5 className="text-secondary">No se han registrado trayectorias educativas</h5>
                                <p className="text-muted small">Cree una nueva trayectoria asociada a una instancia de evaluación para comenzar.</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {trayectorias.filter(tray => esDocenteOAdmin || tray.visible_estudiante == 1).map((tray) => (
                                    <div key={tray.id} className="col-12 col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 hover-shadow-sm transition-all" style={{ borderLeft: '4px solid #198754' }}>
                                            <div className="card-body d-flex flex-column justify-content-between p-3">
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h5 className="card-title fw-bold text-dark mb-0 fs-6">{tray.titulo}</h5>
                                                        <span className={`badge rounded-pill ${tray.visible_estudiante == 1 ? 'bg-success-subtle text-success border border-success' : 'bg-light text-secondary border'}`} style={{ fontSize: '0.65rem' }}>
                                                            {tray.visible_estudiante == 1 ? 'Visible' : 'Oculto'}
                                                        </span>
                                                    </div>

                                                    {tray.instancia_nombre && (
                                                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle mb-2" style={{ fontSize: '0.7rem' }}>
                                                            <i className="fa-solid fa-calendar-check me-1"></i>
                                                            {tray.instancia_nombre}
                                                        </span>
                                                    )}

                                                    <p className="card-text text-secondary small mb-3 text-truncate-2" style={{ height: '38px', overflow: 'hidden' }}>
                                                        {tray.descripcion || <em className="text-muted">Sin descripción</em>}
                                                    </p>

                                                    <span className="badge bg-light text-dark border mb-3" style={{ fontSize: '0.75rem' }}>
                                                        <i className="fa-solid fa-list-check text-success me-1"></i>
                                                        {tray.cantidad_criterios} {tray.cantidad_criterios === 1 ? 'nivel' : 'niveles'}
                                                    </span>
                                                </div>

                                                <div className="border-top pt-2 d-flex justify-content-end gap-1">
                                                    {esDocenteOAdmin ? (
                                                        <>
                                                            <button
                                                                className="btn btn-sm btn-primary rounded-pill px-2 py-1"
                                                                onClick={() => iniciarEvaluacion(tray)}
                                                                title="Evaluar Estudiantes"
                                                                type="button"
                                                                style={{ fontSize: '0.75rem' }}
                                                            >
                                                                <i className="fa-solid fa-check-double me-1"></i> Evaluar
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary rounded-pill p-1 px-2"
                                                                onClick={() => iniciarEdicion(tray)}
                                                                title="Editar"
                                                                type="button"
                                                                style={{ fontSize: '0.75rem' }}
                                                            >
                                                                <i className="fa-solid fa-pencil"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger rounded-pill p-1 px-2"
                                                                onClick={() => eliminarTrayectoria(tray.id)}
                                                                title="Eliminar"
                                                                type="button"
                                                                style={{ fontSize: '0.75rem' }}
                                                            >
                                                                <i className="fa-solid fa-trash"></i>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            className="btn btn-sm btn-outline-success rounded-pill px-3"
                                                            onClick={() => verDetalleTrayectoria(tray)}
                                                            type="button"
                                                        >
                                                            Ver Niveles <i className="fa-solid fa-chevron-right ms-1"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* MODO CREACIÓN / EDICIÓN DE TRAYECTORIA */}
                {!espera && modo === 'crear' && (
                    <form onSubmit={guardarTrayectoria} className="needs-validation">
                        <div className="row g-3">
                            <div className="col-md-6 col-12">
                                <label className="form-label fw-semibold text-dark">Instancia de Evaluación *</label>
                                <select
                                    className="form-select rounded-3"
                                    onChange={(e) => setIdInstancia(e.target.value)}
                                    required
                                    value={idInstancia}
                                >
                                    <option value="">-- Seleccionar Instancia --</option>
                                    {instancias.map(inst => (
                                        <option key={inst.id} value={inst.id}>
                                            {inst.nombre_instancia || `Instancia #${inst.id}`} {inst.tipo_calificacion ? `(${inst.tipo_calificacion})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-6 col-12">
                                <label className="form-label fw-semibold text-dark">Título del Registro de Trayectoria *</label>
                                <input
                                    className="form-control rounded-3"
                                    onChange={(e) => setTitulo(e.target.value)}
                                    placeholder="Ej: Registro de Trayectoria Pedagógica Primer Trimestre..."
                                    required
                                    type="text"
                                    value={titulo}
                                />
                            </div>

                            <div className="col-12 col-md-8">
                                <label className="form-label fw-semibold text-dark">Descripción o Detalle</label>
                                <textarea
                                    className="form-control rounded-3"
                                    onChange={(e) => setDescripcion(e.target.value)}
                                    placeholder="Detalles o marco de referencia de la trayectoria..."
                                    rows="2"
                                    value={descripcion}
                                />
                            </div>

                            <div className="col-12 col-md-4 d-flex align-items-end">
                                <div className="form-check form-switch mb-2">
                                    <input
                                        className="form-check-input"
                                        id="visibleEstudianteTraySwitch"
                                        onChange={(e) => setVisibleEstudiante(e.target.checked)}
                                        type="checkbox"
                                        checked={visibleEstudiante}
                                    />
                                    <label className="form-check-label fw-semibold text-dark" htmlFor="visibleEstudianteTraySwitch">
                                        Visible para Estudiantes
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* NIVELES DINÁMICOS */}
                        <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold text-dark mb-0">Niveles de la Trayectoria</h6>
                                <button
                                    className="btn btn-sm btn-outline-success rounded-pill px-3"
                                    onClick={agregarCriterioFila}
                                    type="button"
                                >
                                    <i className="fa-solid fa-plus me-1"></i> Agregar Nivel
                                </button>
                            </div>

                            <div className="table-responsive">
                                <table className="table align-middle">
                                    <thead>
                                        <tr className="table-light">
                                            <th style={{ width: '35%' }}>Nivel *</th>
                                            <th style={{ width: '60%' }}>Descripción del nivel de logro</th>
                                            <th style={{ width: '5%' }} className="text-center"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {criteriosTemporales.map((crit, index) => {
                                            const opcionesBase = obtenerOpcionesNivel(idInstancia);
                                            const valActual = (crit.criterio || '').trim();
                                            const matchExacto = opcionesBase.find(op => String(op).trim().toLowerCase() === valActual.toLowerCase());

                                            let opcionesFinales = [...opcionesBase];
                                            if (valActual !== '' && !matchExacto) {
                                                opcionesFinales.push(valActual);
                                            }

                                            const valorSeleccionado = matchExacto || crit.criterio;

                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        {opcionesFinales.length > 0 ? (
                                                            <select
                                                                className="form-select form-select-sm rounded-2"
                                                                onChange={(e) => handleCriterioCambio(index, 'criterio', e.target.value)}
                                                                required
                                                                value={valorSeleccionado}
                                                            >
                                                                <option value="">-- Seleccionar Nivel --</option>
                                                                {opcionesFinales.map((op, idx) => (
                                                                    <option key={idx} value={op}>{op}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <input
                                                                className="form-control form-control-sm rounded-2"
                                                                onChange={(e) => handleCriterioCambio(index, 'criterio', e.target.value)}
                                                                placeholder="Ej: Logrado, MS, 8..."
                                                                required
                                                                type="text"
                                                                value={crit.criterio}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            className="form-control form-control-sm rounded-2"
                                                            onChange={(e) => handleCriterioCambio(index, 'descripcion', e.target.value)}
                                                            placeholder="Descripción de los aspectos del nivel..."
                                                            type="text"
                                                            value={crit.descripcion}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-link text-danger p-0"
                                                            onClick={() => eliminarCriterioFila(index)}
                                                            type="button"
                                                        >
                                                            <i className="fa-solid fa-trash-can"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                            <button
                                className="btn btn-secondary rounded-pill px-4"
                                onClick={() => setModo('list')}
                                type="button"
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-success rounded-pill px-4"
                                type="submit"
                            >
                                Guardar Trayectoria
                            </button>
                        </div>
                    </form>
                )}

                {/* MODO PANEL DE EVALUACIÓN PARA DOCENTE */}
                {!espera && modo === 'evaluar' && trayectoriaSeleccionada && (
                    <div>
                        {!esPeriodoActivo(trayectoriaSeleccionada) && (
                            <div className="alert alert-warning mb-4 shadow-sm border-warning">
                                <i className="fa-solid fa-clock me-2"></i>
                                <strong>Período inactivo:</strong> El período de registro de esta instancia ha finalizado o aún no está activo. Las evaluaciones están deshabilitadas.
                            </div>
                        )}

                        <div className="alert bg-light border-start border-success border-4 mb-4 p-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <h5 className="fw-bold text-dark mb-0">{trayectoriaSeleccionada.titulo}</h5>
                                {trayectoriaSeleccionada.instancia_nombre && (
                                    <span className="badge bg-success text-white">
                                        Instancia: {trayectoriaSeleccionada.instancia_nombre}
                                    </span>
                                )}
                            </div>
                            <p className="text-secondary small mb-0">{trayectoriaSeleccionada.descripcion}</p>
                        </div>

                        <div className="row">
                            {/* Panel Estudiantes */}
                            <div className="col-12 col-md-4 border-end">
                                <h6 className="fw-bold text-dark mb-3">Estudiantes del Curso ({estudiantesCurso.length})</h6>
                                <div className="list-group rounded-3 shadow-sm border" style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
                                    {estudiantesCurso.length === 0 ? (
                                        <div className="p-3 text-center text-muted small">No hay estudiantes inscriptos</div>
                                    ) : (
                                        estudiantesCurso.map(est => {
                                            const estaEvaluado = estudiantesEvaluadosIds.some(id => String(id) === String(est.id));
                                            return (
                                                <button
                                                    key={est.id}
                                                    className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between p-2 border-0 border-bottom ${estudianteSeleccionado?.id === est.id ? 'active bg-success text-white' : ''}`}
                                                    onClick={() => seleccionarEstudianteParaEvaluar(est)}
                                                    type="button"
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <PerfilLogo usuario={est} version="muro" configuracion={configuracion} />
                                                        <div className="text-start">
                                                            <p className="mb-0 fw-semibold small text-inherit">{est.apellido}, {est.nombre}</p>
                                                            <span className="text-inherit opacity-75" style={{ fontSize: '0.7rem' }}>DNI: {est.documento}</span>
                                                        </div>
                                                    </div>

                                                    {estaEvaluado && (
                                                        <span
                                                            className={`badge rounded-pill ${estudianteSeleccionado?.id === est.id ? 'bg-light text-success' : 'bg-success-subtle text-success border border-success'}`}
                                                            style={{ fontSize: '0.65rem' }}
                                                            title="Evaluación registrada"
                                                        >
                                                            <i className="fa-solid fa-circle-check me-1"></i>Evaluado
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* Panel de Evaluación */}
                            <div className="col-12 col-md-8 ps-md-4 mt-3 mt-md-0">
                                {!estudianteSeleccionado ? (
                                    <div className="h-100 d-flex flex-column justify-content-center align-items-center text-center p-5 bg-light rounded-3">
                                        <i className="fa-solid fa-user-check text-muted mb-3" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
                                        <h6 className="text-secondary fw-semibold">Selecciona un estudiante para evaluar</h6>
                                        <p className="text-muted small">Haz clic en un alumno de la lista de la izquierda para calificarlo.</p>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                                            {estudianteSeleccionado.imagen_perfil ? (
                                                <img
                                                    src={`${CONFIG.API_URL}/${estudianteSeleccionado.imagen_perfil}`}
                                                    alt="Perfil"
                                                    style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '48px', height: '48px', borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #198754, #0d6efd)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontWeight: 'bold', fontSize: '1rem', flexShrink: 0
                                                }}>
                                                    {(estudianteSeleccionado.nombre?.charAt(0) || '').toUpperCase()}
                                                    {(estudianteSeleccionado.apellido?.charAt(0) || '').toUpperCase()}
                                                </div>
                                            )}
                                            <div>
                                                <h5 className="fw-bold text-dark mb-0">{estudianteSeleccionado.nombre} {estudianteSeleccionado.apellido}</h5>
                                                <p className="text-secondary small mb-0">DNI: {estudianteSeleccionado.documento}</p>
                                            </div>
                                        </div>

                                        <div className="criterios-evaluacion-lista">
                                            {criteriosEvaluacion.map((crit, index) => (
                                                <div key={crit.id} className="card border mb-3 rounded-3 shadow-2xs">
                                                    <div className="card-body p-3">
                                                        <div className="row g-3 align-items-center">
                                                            <div className="col-md-7 col-12">
                                                                <h6 className="fw-bold text-dark mb-1">{crit.criterio}</h6>
                                                                <p className="text-secondary small mb-0">{crit.descripcion || <em className="text-muted">Sin descripción</em>}</p>
                                                            </div>
                                                            <div className="col-md-5 col-12">
                                                                {/* CAMBIO DE PUNTAJE POR NIVEL ALCANZADO CON REGLA EXCLUYENTE */}
                                                                <label className="form-label small fw-semibold text-dark mb-1">
                                                                    Nivel alcanzado
                                                                </label>
                                                                <select
                                                                    className={`form-select form-select-sm rounded-2 fw-bold ${crit.calificacion === 'Logrado' ? 'border-success text-success bg-success-subtle' : 'text-secondary'}`}
                                                                    disabled={!esPeriodoActivo(trayectoriaSeleccionada)}
                                                                    onChange={(e) => handleEvaluacionCambio(index, 'calificacion', e.target.value)}
                                                                    value={crit.calificacion || '-'}
                                                                >
                                                                    <option value="-">-</option>
                                                                    <option value="Logrado">Logrado</option>
                                                                </select>
                                                            </div>
                                                            <div className="col-12 mt-2">
                                                                <textarea
                                                                    className="form-control form-control-sm rounded-2"
                                                                    disabled={!esPeriodoActivo(trayectoriaSeleccionada)}
                                                                    onChange={(e) => handleEvaluacionCambio(index, 'comentario', e.target.value)}
                                                                    placeholder="Comentarios de retroalimentación para este criterio..."
                                                                    rows="2"
                                                                    value={crit.comentario || ''}
                                                                />
                                                            </div>
                                                        </div>
                                                        {crit.fecha_evaluacion && (
                                                            <div className="mt-2 text-end text-muted" style={{ fontSize: '0.65rem' }}>
                                                                <i className="fa-solid fa-clock me-1"></i>
                                                                Última actualización: {crit.fecha_evaluacion}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                            <button
                                                className="btn btn-success rounded-pill px-4"
                                                disabled={!esPeriodoActivo(trayectoriaSeleccionada)}
                                                onClick={guardarEvaluacionEstudiante}
                                                type="button"
                                            >
                                                <i className="fa-solid fa-floppy-disk me-1"></i> Guardar Evaluación
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODO VISTA DE CRITERIOS (ESTUDIANTES - READ-ONLY) */}
                {!espera && modo === 'ver_estudiante' && trayectoriaSeleccionada && (
                    <div>
                        <div className="alert bg-light border-start border-success border-4 mb-4 p-3">
                            <h5 className="fw-bold text-dark mb-1">{trayectoriaSeleccionada.titulo}</h5>
                            {trayectoriaSeleccionada.instancia_nombre && (
                                <span className="badge bg-success text-white mb-2">
                                    Instancia: {trayectoriaSeleccionada.instancia_nombre}
                                </span>
                            )}
                            <p className="text-secondary small mb-0">{trayectoriaSeleccionada.descripcion}</p>
                        </div>

                        <h6 className="fw-bold text-dark mb-3">Criterios de evaluación:</h6>
                        <div className="row g-3">
                            {criteriosEvaluacion.map(crit => (
                                <div key={crit.id} className="col-12">
                                    <div className="card border rounded-3 p-3 bg-light-subtle">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h6 className="fw-bold text-dark mb-0">{crit.criterio}</h6>
                                            <span className={`badge px-3 py-1 ${crit.calificacion === 'Logrado' ? 'bg-success text-white' : 'bg-secondary-subtle text-secondary'}`} style={{ fontSize: '0.8rem' }}>
                                                Nivel alcanzado: {crit.calificacion || '-'}
                                            </span>
                                        </div>
                                        <p className="text-secondary small mb-0">{crit.descripcion || <em className="text-muted">Sin detalles</em>}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default Trayectorias;
